import React from 'react';
import { formatString, type StandApartRoleChoice } from '@deadpan/shared';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';
import { StandApartCards, STAND_APART_CARD_LETTERS } from './StandApartCards';

export const StandApartGame: React.FC = () => {
  const { socket, currentRoom, gameState, myStandApartCardIndex, myPlayerId } = useSocket();
  const t = useTranslations();
  const sa = t.games.standApart;
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(myStandApartCardIndex);

  const stage = gameState?.standApartStage;
  const round = currentRoom?.currentRound ?? 1;
  const submittedIds = gameState?.standApartSubmittedPlayerIds ?? [];
  const hasSubmitted = Boolean(myPlayerId && submittedIds.includes(myPlayerId));
  const cardCount = gameState?.standApartCardCount ?? currentRoom?.nashConfig?.cardCount ?? 4;

  React.useEffect(() => {
    if (myStandApartCardIndex != null) {
      setSelectedIndex(myStandApartCardIndex);
    } else if (stage === 'select' && !hasSubmitted) {
      setSelectedIndex(null);
    }
  }, [myStandApartCardIndex, stage, hasSubmitted, round]);

  if (!gameState || !currentRoom) {
    return null;
  }

  const playersById = Object.fromEntries(currentRoom.players.map((p) => [p.id, p]));
  const participants = currentRoom.players.filter((p) => !p.isSpectator);
  const isHost = currentRoom.hostId === myPlayerId;
  const isSpectator = currentRoom.players.find((p) => p.id === myPlayerId)?.isSpectator;
  const canPick = stage === 'select' && !isSpectator && !hasSubmitted;
  const revealed = stage === 'reveal';
  const outcome = gameState.standApartOutcome;
  const chooserId = revealed ? gameState.standApartChooserId : undefined;
  const partnerId = revealed ? gameState.standApartPartnerId : undefined;
  const askerId = revealed ? gameState.standApartAskerId : undefined;
  const answererId = revealed ? gameState.standApartAnswererId : undefined;
  const rolesChosen = Boolean(askerId && answererId);
  const chooserName = chooserId ? playersById[chooserId]?.name ?? '—' : '—';
  const partnerName = partnerId ? playersById[partnerId]?.name ?? '—' : '—';
  const askerName = askerId ? playersById[askerId]?.name ?? '—' : '—';
  const answererName = answererId ? playersById[answererId]?.name ?? '—' : '—';
  const isChooser = Boolean(myPlayerId && myPlayerId === chooserId);
  const selectedLetter =
    selectedIndex != null ? STAND_APART_CARD_LETTERS[selectedIndex] ?? String(selectedIndex + 1) : null;

  const handleConfirm = () => {
    if (!socket || !canPick || selectedIndex == null) return;
    socket.emit('client:submit-stand-apart-pick', selectedIndex);
  };

  const handleChooseRole = (choice: StandApartRoleChoice) => {
    if (!socket || !isChooser || rolesChosen) return;
    socket.emit('client:stand-apart-choose-role', choice);
  };

  const handleNextRound = () => {
    if (!socket || !isHost) return;
    socket.emit('client:stand-apart-next-round');
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <h2 className="text-[clamp(1.5rem,8vw,3rem)] font-bold bg-gradient-to-r from-fuchsia-600 via-rose-600 to-orange-500 bg-clip-text text-transparent text-center mb-1">
        {sa.title}
      </h2>
      <p className="text-xs sm:text-sm text-gray-500 mb-3">
        {formatString(sa.roundLabel, { round: String(round) })}
      </p>

      {stage === 'select' && (
        <div className="w-full mb-3 text-center">
          <h3 className="text-base sm:text-lg font-bold text-gray-900">{sa.selectTitle}</h3>
        </div>
      )}

      {revealed && outcome === 'void' && (
        <div className="w-full mb-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-center">
          <h3 className="text-lg font-bold text-amber-900">{sa.voidTitle}</h3>
          <p className="text-sm text-amber-800 mt-1">{sa.voidBody}</p>
        </div>
      )}

      {revealed && outcome === 'roles' && (
        <h3 className="text-lg sm:text-xl font-bold text-center text-gray-900 mb-3">{sa.revealTitle}</h3>
      )}

      <StandApartCards
        cardCount={cardCount}
        selectedIndex={selectedIndex}
        submitted={hasSubmitted}
        revealed={Boolean(revealed)}
        picks={gameState.standApartPicks}
        uniqueCardIndexes={gameState.standApartUniqueCardIndexes}
        chooserId={chooserId}
        playersById={playersById}
        disabled={!canPick}
        onSelect={setSelectedIndex}
      />

      {stage === 'select' && (
        <div className="w-full mt-4 flex flex-col items-center gap-3">
          {canPick ? (
            <>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={selectedIndex == null}
                className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-fuchsia-600 to-rose-600 hover:from-fuchsia-700 hover:to-rose-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed"
              >
                {sa.confirmPick}
              </button>
            </>
          ) : isSpectator ? (
            <p className="text-sm text-gray-600 text-center">{sa.waitingForOthers}</p>
          ) : (
            <p className="text-sm font-semibold text-green-700">
              {selectedLetter
                ? formatString(sa.yourPick, { card: selectedLetter })
                : sa.pickSubmitted}
            </p>
          )}
          <p className="text-sm text-gray-600">
            {formatString(sa.submittedProgress, {
              submitted: String(submittedIds.length),
              total: String(participants.length),
            })}
          </p>
        </div>
      )}

      {revealed && outcome === 'roles' && (
        <div className="w-full mt-4 space-y-4">
          <h3 className="text-xl sm:text-2xl font-bold text-center text-gray-900">{sa.resultTitle}</h3>
          {rolesChosen ? (
            <>
              <p className="text-center text-base sm:text-lg font-semibold text-gray-800">
                {formatString(sa.askPrompt, { asker: askerName, answerer: answererName })}
              </p>
              {myPlayerId === askerId && (
                <p className="text-center text-sm font-semibold text-indigo-700">{sa.youAreAsker}</p>
              )}
              {myPlayerId === answererId && (
                <p className="text-center text-sm font-semibold text-pink-700">{sa.youAreAnswerer}</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-center">
                  <p className="text-xs font-semibold text-red-700">{sa.askerLabel}</p>
                  <p className="text-lg font-bold text-red-900 break-words">{askerName}</p>
                </div>
                <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-center">
                  <p className="text-xs font-semibold text-amber-700">{sa.answererLabel}</p>
                  <p className="text-lg font-bold text-amber-900 break-words">{answererName}</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-center text-base sm:text-lg font-semibold text-gray-800">
                {isChooser
                  ? sa.chooserChooseHint
                  : formatString(sa.waitingForChooser, { chooser: chooserName })}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border-2 border-pink-200 bg-pink-50 p-3 min-h-[3.5rem] flex items-center justify-center text-center">
                  <p className="text-lg font-bold text-pink-900 break-words">{chooserName}</p>
                </div>
                <div className="rounded-xl border-2 border-gray-200 bg-gray-50 p-3 min-h-[3.5rem] flex items-center justify-center text-center">
                  <p className="text-lg font-bold text-gray-900 break-words">{partnerName}</p>
                </div>
              </div>
              {isChooser && (
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => handleChooseRole('ask')}
                    className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
                  >
                    {sa.chooseAsk}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChooseRole('answer')}
                    className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                  >
                    {sa.chooseAnswer}
                  </button>
                </div>
              )}
            </>
          )}
          {rolesChosen && isHost ? (
            <button
              type="button"
              onClick={handleNextRound}
              className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-fuchsia-600 to-rose-600 hover:from-fuchsia-700 hover:to-rose-700"
            >
              {sa.nextRound}
            </button>
          ) : rolesChosen ? (
            <p className="text-sm text-gray-600 text-center">{sa.waitingForHostNextRound}</p>
          ) : null}
        </div>
      )}
    </div>
  );
};
